import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileQuestion, Home, Mail, SearchX } from 'lucide-react';
import Footer from '@/shared/components/Footer';
import Navbar from '@/shared/components/Navbar';
import { Badge } from '@/shared/components/ui/Badge';
import { ButtonArrow } from '@/shared/components/ui/ButtonArrow';
import { Button } from '@/shared/components/ui/Button';
import { useI18n } from '@/shared/contexts/I18nProvider';

const errorRevealDelayMs = 1600;

type BreakingThreadProps = {
  isBroken: boolean;
};

const BreakingThread = ({ isBroken }: BreakingThreadProps) => {
  const { content } = useI18n();

  return (
    <div
      className={[
        'not-found-thread relative min-h-[390px] overflow-hidden rounded-lg border bg-background-card/75 p-6 shadow-sm backdrop-blur transition-colors duration-500',
        isBroken ? 'not-found-thread--broken' : ''
      ].join(' ')}
      aria-hidden="true"
    >
      <div className="not-found-thread__glow" />

      <div className="relative z-10 flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-[0.18em] text-text-tertiary">
          {content.notFound.routeSignal}
        </span>
        <span className="not-found-thread__state">
          {isBroken ? '404' : content.notFound.live}
        </span>
      </div>

      <div className="not-found-thread__rail">
        <span className="not-found-thread__segment not-found-thread__segment--top" />
        <span className="not-found-thread__segment not-found-thread__segment--bottom" />
        <span className="not-found-thread__pulse not-found-thread__pulse--one" />
        <span className="not-found-thread__pulse not-found-thread__pulse--two" />
        <span className="not-found-thread__snap" />
        <span className="not-found-thread__label">404</span>
      </div>

      <div className="not-found-thread__caption">
        {isBroken
          ? content.notFound.connectionLost
          : content.notFound.checkingRoute}
      </div>
    </div>
  );
};

const NotFoundPage = () => {
  const { content, localizedPath } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const requestedRoute = `${location.pathname}${location.search}${location.hash}`;
  const [isBroken, setIsBroken] = useState(false);

  useEffect(() => {
    setIsBroken(false);

    const timerId = window.setTimeout(() => {
      setIsBroken(true);
    }, errorRevealDelayMs);

    return () => window.clearTimeout(timerId);
  }, [requestedRoute]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      void navigate(-1);
      return;
    }

    void navigate(localizedPath('/'));
  };

  return (
    <div
      className={[
        'not-found-page min-h-screen bg-background-primary text-text-primary antialiased',
        isBroken ? 'not-found-page--broken' : ''
      ].join(' ')}
    >
      <Navbar />

      <main className="relative z-10 pt-32 sm:pt-28">
        <section className="not-found-hero-section border-b border-border-muted bg-transparent py-14 transition-colors duration-500 sm:py-16">
          <div className="mx-auto grid min-h-[calc(100vh-12rem)] max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)] lg:px-8">
            <div>
              <Badge
                variant="outline"
                className={[
                  'not-found-badge mb-5 bg-background-card/70 font-mono uppercase tracking-[0.18em] transition-colors duration-500',
                  isBroken
                    ? 'border-accent-error/45 text-accent-error'
                    : 'border-accent-primary/30 text-accent-primary'
                ].join(' ')}
              >
                {isBroken
                  ? content.notFound.pageBadge
                  : content.notFound.routeCheckBadge}
              </Badge>

              <h1 className="not-found-title max-w-3xl text-4xl font-light tracking-tight transition-colors duration-500 sm:text-5xl lg:text-6xl">
                {content.notFound.title}
              </h1>
              <p className="not-found-copy mt-5 max-w-2xl text-lg leading-8 text-text-secondary transition-colors duration-500">
                {content.notFound.description}
              </p>

              <div className="not-found-actions mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild size="lg" className="gap-2 font-mono">
                  <Link to={localizedPath('/')}>
                    <Home className="h-5 w-5" />
                    {content.notFound.goHome}
                  </Link>
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="lg"
                  className="gap-2 font-mono !text-white shadow-sm shadow-shadow"
                  onClick={handleGoBack}
                >
                  <ArrowLeft className="h-5 w-5" />
                  {content.notFound.goBack}
                </Button>
              </div>

              <div className="mt-10 grid max-w-4xl gap-3 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                <div className="not-found-card flex items-start gap-4 rounded-lg border border-border-primary bg-background-card p-5 shadow-sm transition-colors duration-500">
                  <div
                    className={[
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors duration-500',
                      isBroken
                        ? 'bg-state-error-bg text-accent-error'
                        : 'bg-state-info-bg text-accent-primary'
                    ].join(' ')}
                  >
                    <SearchX className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-text-tertiary">
                      {content.notFound.requestedRoute}
                    </p>
                    <p className="mt-2 break-all font-mono text-sm text-text-secondary">
                      {requestedRoute}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3">
                  <Link
                    to={localizedPath('/')}
                    state={{ scrollToSection: 'platform' }}
                    className="not-found-card group flex items-center justify-between rounded-md border border-border-muted bg-background-secondary px-4 py-3 transition-colors hover:border-accent-primary/50 hover:bg-background-hover"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <FileQuestion className="h-5 w-5 shrink-0 text-accent-primary transition-colors duration-500" />
                      <span>
                        <span className="block font-medium text-text-primary">
                          {content.notFound.explorePlatformTitle}
                        </span>
                        <span className="text-sm text-text-secondary">
                          {content.notFound.explorePlatformDescription}
                        </span>
                      </span>
                    </span>
                    <ButtonArrow className="h-4 w-4 shrink-0 text-text-tertiary transition-colors group-hover:text-accent-primary" />
                  </Link>

                  <Link
                    to={localizedPath('/cases')}
                    className="not-found-card group flex items-center justify-between rounded-md border border-border-muted bg-background-secondary px-4 py-3 transition-colors hover:border-accent-primary/50 hover:bg-background-hover"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <FileQuestion className="h-5 w-5 shrink-0 text-accent-primary transition-colors duration-500" />
                      <span>
                        <span className="block font-medium text-text-primary">
                          {content.notFound.viewCasesTitle}
                        </span>
                        <span className="text-sm text-text-secondary">
                          {content.notFound.viewCasesDescription}
                        </span>
                      </span>
                    </span>
                    <ButtonArrow className="h-4 w-4 shrink-0 text-text-tertiary transition-colors group-hover:text-accent-primary" />
                  </Link>

                  <Link
                    to={localizedPath('/contact')}
                    className="not-found-card group flex items-center justify-between rounded-md border border-border-muted bg-background-secondary px-4 py-3 transition-colors hover:border-accent-primary/50 hover:bg-background-hover"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <Mail className="h-5 w-5 shrink-0 text-accent-primary transition-colors duration-500" />
                      <span>
                        <span className="block font-medium text-text-primary">
                          {content.notFound.contactTeamTitle}
                        </span>
                        <span className="text-sm text-text-secondary">
                          {content.notFound.contactTeamDescription}
                        </span>
                      </span>
                    </span>
                    <ButtonArrow className="h-4 w-4 shrink-0 text-text-tertiary transition-colors group-hover:text-accent-primary" />
                  </Link>
                </div>
              </div>
            </div>

            <BreakingThread isBroken={isBroken} />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NotFoundPage;
